import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { api } from "../lib/api";
import { usePersistedPage } from "../lib/usePersistedPage";
import { Pager } from "../components/Pager";
import { SortableTh } from "../components/SortableTh";
import { useAutoPageSize } from "../lib/useAutoPageSize";
import { useSortable } from "../lib/useSortable";
import { SECTOR_LABEL, STAGE_LABEL, SECTOR_OPTIONS, STAGE_OPTIONS } from "../lib/enums";

type ProjectRow = {
  id: number;
  slug: string;
  sector: string;
  stage: "listed" | "pipeline" | "early";
  name_zh: string;
  name_en: string;
  ticker: string | null;
  return_multiple: number | string | null;
  sort_order: number;
  active: boolean;
  updated_at: string;
};

export function ProjectsListPage() {
  const nav = useNavigate();
  const [rows, setRows] = useState<ProjectRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // page 持久化：刷新 / 从明细返回都停留在原页
  const [page, setPage] = usePersistedPage("kexi_proj_page");
  // 过滤项持久化到 localStorage：刷新、进入编辑页再返回都保留
  const [sectorF, setSectorF] = useState(() => localStorage.getItem("kexi_proj_sector") || "");
  const [stageF, setStageF] = useState(() => localStorage.getItem("kexi_proj_stage") || "");
  const { containerRef, pageSize } = useAutoPageSize();

  const load = () => {
    setError(null);
    api.get<ProjectRow[]>("/api/admin/projects").then(setRows).catch((e) => setError(String(e)));
  };

  useEffect(load, []);
  // 切换筛选回第 1 页：直接在筛选 onChange 里 setPage(1)，
  // 不用「监听 effect + 首挂守卫」（StrictMode 双挂载下不可靠，会误覆盖持久化页码）。

  const filtered = (rows ?? []).filter(
    (r) => (!sectorF || r.sector === sectorF) && (!stageF || r.stage === stageF),
  );
  const { sorted, sortKey, sortDir, toggle } = useSortable(filtered);
  // 持久化的 page 可能超过当前总页数（数据变少时），自动收敛到最后有效页
  useEffect(() => {
    if (!rows) return;
    const maxPage = Math.max(1, Math.ceil(sorted.length / pageSize));
    if (page > maxPage) setPage(maxPage);
  }, [rows, sorted.length, pageSize, page]);
  const pageRows = rows ? sorted.slice((page - 1) * pageSize, page * pageSize) : null;

  return (
    <div>
      <div className="page-header">
        <div className="page-title">投资项目</div>
        <div className="page-actions" style={{ alignItems: "center", gap: 10 }}>
          <select value={stageF}
            onChange={(e) => { setStageF(e.target.value); localStorage.setItem("kexi_proj_stage", e.target.value); setPage(1); }}
            style={{ height: 32, padding: "4px 11px", lineHeight: 1.2 }}>
            <option value="">全部阶段</option>
            {STAGE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select value={sectorF}
            onChange={(e) => { setSectorF(e.target.value); localStorage.setItem("kexi_proj_sector", e.target.value); setPage(1); }}
            style={{ height: 32, padding: "4px 11px", lineHeight: 1.2 }}>
            <option value="">全部赛道</option>
            {SECTOR_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <Link className="btn btn-primary" to="/projects/new">+ 新建</Link>
        </div>
      </div>

      {error && <div className="error" style={{ marginBottom: 12 }}>{error}</div>}
      {!rows && !error && <div className="muted">加载中…</div>}

      {rows && (
        <div ref={containerRef}>
          <table className="table table-fixed">
            <colgroup>
              <col style={{ width: "13%" }} />
              <col style={{ width: "19%" }} />
              <col style={{ width: "19%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "11%" }} />
              <col style={{ width: "11%" }} />
              <col style={{ width: "7%" }} />
              <col style={{ width: "5%" }} />
              <col style={{ width: "5%" }} />
            </colgroup>
            <thead>
              <tr>
                {(() => { const s = { active: sortKey, dir: sortDir, toggle }; return <>
                <SortableTh sortKey="slug" {...s}>Slug</SortableTh>
                <SortableTh sortKey="name_zh" {...s}>中文名</SortableTh>
                <SortableTh sortKey="name_en" {...s}>英文名</SortableTh>
                <SortableTh sortKey="stage" {...s}>阶段</SortableTh>
                <SortableTh sortKey="sector" {...s}>赛道</SortableTh>
                <SortableTh sortKey="ticker" {...s}>股票代码</SortableTh>
                <SortableTh sortKey="return_multiple" {...s} style={{ textAlign: "right" }}>倍数</SortableTh>
                <SortableTh sortKey="sort_order" {...s} style={{ textAlign: "right" }}>序</SortableTh>
                <SortableTh sortKey="active" {...s}>状态</SortableTh>
                </>; })()}
              </tr>
            </thead>
            <tbody>
              {pageRows!.map((r) => (
                <tr key={r.slug} className="row-link" onClick={() => nav(`/projects/${r.slug}`)}>
                  <td className="slug ellip">{r.slug}</td>
                  <td className="ellip">{r.name_zh}</td>
                  <td className="muted ellip">{r.name_en}</td>
                  <td><span className="pill pill-nowrap">{STAGE_LABEL[r.stage] ?? r.stage}</span></td>
                  <td className="ellip">{SECTOR_LABEL[r.sector] ?? r.sector}</td>
                  <td className="slug ellip">{r.ticker || <span className="muted">—</span>}</td>
                  <td style={{ textAlign: "right" }}>
                    {r.return_multiple == null ? "" : `${Number(r.return_multiple)}×`}
                  </td>
                  <td style={{ textAlign: "right" }}>{r.sort_order}</td>
                  <td>
                    {r.active
                      ? <span className="success">●</span>
                      : <span className="muted">已删</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <Pager page={page} pageSize={pageSize} total={filtered.length} onChange={setPage} />
        </div>
      )}
    </div>
  );
}
